import { useCallback, useState } from 'react';
import { api, type Insight, type SubscriptionSummary, type Transaction, type Wrap } from '../api.ts';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import { PullToRefresh } from '../PullToRefresh.tsx';
import {
  Card, Empty, ErrorState, PeriodPicker, ProvenanceBadge,
  SectionTitle, Sheet, Skeleton, TransactionRow, type PeriodKey,
} from '../components.tsx';
import { formatDate } from '../format.ts';
import { BreakdownChart, type BreakdownBucket, type Granularity } from '../BreakdownChart.tsx';

/** Names the division being shown, so the axis needs no explaining. */
const GRANULARITY_TITLE: Record<Granularity, { en: string; ar: string }> = {
  hour: { en: 'By hour', ar: 'حسب الساعة' },
  day: { en: 'By day', ar: 'حسب اليوم' },
  week: { en: 'By week', ar: 'حسب الأسبوع' },
  month: { en: 'By month', ar: 'حسب الشهر' },
};

/*
 * Every insight kind this screen generates now has a place, and none of them
 * is a paragraph:
 *
 *   top_categories, category_total  → the stacked bar and its legend
 *   category_change                 → the delta beside each category
 *   merchant_total                  → most spent at
 *   subscriptions                   → the subscriptions section
 *   savings_rate                    → the Kept tile, which is the same
 *                                     arithmetic: (income − spent) / income
 *   anomaly                         → unusual days, below
 *
 * Which is why nothing here filters a list of leftovers — the only kind that
 * still renders as its own row is the anomaly, and it renders as figures too.
 */

/**
 * A day's transactions, opened from a spike.
 *
 * The row says a day cost four times a normal one; the obvious next question
 * is what was bought, and before this there was no way to ask it.
 */
function DaySheet({ day, currency, onClose }: {
  day: string; currency: string; onClose: () => void;
}): JSX.Element {
  const { locale, user, t } = useApp();
  const money = useMoney();
  // The day is the account's, not the browser's: a boundary read in the wrong
  // zone pulls in the small hours of the next day and drops the evening of
  // this one, which would make the sheet disagree with the figure that opened it.
  const tz = user?.tzOffsetMinutes ?? 0;
  const startedAt = Date.parse(`${day}T00:00:00Z`) - tz * 60_000;
  const from = new Date(startedAt).toISOString();
  const to = new Date(startedAt + 86_400_000).toISOString();

  const { data, loading } = useFetch<{ transactions: Transaction[]; totals: { spendingMinor: number } }>(
    `/transactions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=100`,
    [day],
  );

  return (
    <Sheet title={formatDate(`${day}T12:00:00`, locale)} onClose={onClose}>
      <div className="stack">
        {loading && !data && <Skeleton count={4} height={56} />}
        {data && (
          <>
            <Card>
              <div className="row row--between">
                <span className="tiny muted">{t('spent')}</span>
                <span className="strong money" style={{ fontSize: 18 }}>
                  {money(data.totals.spendingMinor, currency)}
                </span>
              </div>
            </Card>
            <Card className="card--flush">
              {data.transactions.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
            </Card>
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * A change against the period before.
 *
 * Takes either two amounts or a ready-made percentage, because category rows
 * arrive with the percentage already computed server-side and the tiles do not.
 *
 * `spend` flips which direction is good: more income is progress, more spending
 * is not. Direction is carried by the arrow as well as the colour — a red
 * number and a green number are the same number to a colourblind reader.
 */
function Delta({ from, to, pct, spend = false }: {
  from?: number; to?: number; pct?: number; spend?: boolean;
}): JSX.Element | null {
  const value = pct ?? (from && from > 0 && to != null ? ((to - from) / from) * 100 : null);
  if (value === null || !Number.isFinite(value)) return null;

  const rounded = Math.round(value);
  // Under a percent either way, "unchanged" is the honest reading; a 0% arrow
  // pointing somewhere is not.
  if (rounded === 0) {
    return <span className="delta delta--flat">— 0%</span>;
  }
  const up = rounded > 0;
  const good = spend ? !up : up;
  return (
    <span className={`delta delta--${good ? 'good' : 'bad'}`}>
      {up ? '▲' : '▼'}{Math.abs(rounded)}%
    </span>
  );
}

/**
 * Insights — statements about the user's money, each traceable to real
 * transactions, each labelled confirmed or estimated.
 */
export function InsightsScreen(): JSX.Element {
  const { t, locale, user, toast, activeAccountId, toastError, categoryLabel, categoryMeta } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [spikeDay, setSpikeDay] = useState<{ day: string; count: number } | null>(null);
  const currency = user?.currency ?? 'AED';

  const { data, error, loading, reload } = useFetch<{
    insights: Insight[];
    period: { key: string; label: string; from: string; to: string };
    comparable: boolean;
    summary: {
      currency: string; spentMinor: number; incomeMinor: number; netMinor: number;
      dailyAverageMinor: number; transactionCount: number;
      previousSpentMinor: number; previousIncomeMinor: number;
    };
    byCategory: {
      category: string; totalMinor: number; count: number; sharePct: number;
      previousMinor: number | null; changePct: number | null;
    }[];
    topMerchants: { merchant: string; display: string | null; totalMinor: number; count: number }[];
    breakdown: { granularity: Granularity; buckets: BreakdownBucket[] };
  }>(`/insights?period=${period}${activeAccountId ? `&accountId=${activeAccountId}` : ''}`, [period, activeAccountId]);
  const { data: subs, reload: reloadSubs } = useFetch<SubscriptionSummary>('/recurring');
  // Subscriptions are not period-scoped, so they never move when the picker
  // does — which makes them exactly the part a refresh has to cover, or half
  // the screen stays as it was.
  const refreshAll = useCallback(
    () => Promise.all([reload(), reloadSubs()]),
    [reload, reloadSubs],
  );

  const dismissRecurring = async (merchant: string) => {
    try {
      await api.post(`/recurring/${encodeURIComponent(merchant)}/dismiss`);
      toast(t('done'));
      reloadSubs();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="screen">
      <h1 style={{ fontSize: 21, fontWeight: 680, marginBottom: 12 }}>{t('insights')}</h1>

      {/* The cycle just gone, closed off. Every other view here answers "how am
          I doing"; this one says what happened, which is the only framing that
          produces "I spent *how much* on delivery?" */}
      <WrapCard />

      <PeriodPicker value={period} onChange={setPeriod} />

      {/* Which days the figures below actually cover.
          The labels alone cannot say it: "This month" follows the pay cycle,
          so it starts on payday rather than the 1st, and "Last month" and
          "Last 30 days" are different windows that overlap without matching.
          `to` is exclusive, so the day shown is the one before it. */}
      {data?.period && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          {formatDate(data.period.from, locale)} – {formatDate(
            new Date(new Date(data.period.to).getTime() - 86_400_000).toISOString(), locale,
          )}
        </div>
      )}

      {loading && !data && <div style={{ marginTop: 16 }}><Skeleton count={3} height={78} /></div>}
      {error && <ErrorState message={error} onRetry={reload} />}

      {/* The four figures the period picker moves.
          They sit above everything because they are the answer; the sections
          below are the working. */}
      {data?.summary && (
        <div className="tiles" style={{ marginTop: 12 }}>
          <div className="tile">
            <span className="tile__label">{t('spent')}</span>
            <span className="tile__value money">
              {money(data.summary.spentMinor, currency, { compact: true })}
            </span>
            <span className="tile__foot">
              {data.comparable
                ? <Delta from={data.summary.previousSpentMinor} to={data.summary.spentMinor} spend />
                : data.period.label}
            </span>
          </div>

          <div className="tile">
            <span className="tile__label">{locale === 'ar' ? 'الدخل' : 'Income'}</span>
            <span className="tile__value money">
              {privateMoney(data.summary.incomeMinor, currency, { compact: true })}
            </span>
            <span className="tile__foot">
              {data.comparable && data.summary.previousIncomeMinor > 0
                ? <Delta from={data.summary.previousIncomeMinor} to={data.summary.incomeMinor} />
                : <>{data.summary.transactionCount} {t('transactionsLower')}</>}
            </span>
          </div>

          <div className="tile">
            {/* Income minus spending. Named "kept" rather than "net" because
                that is what it is once it is a positive number. */}
            <span className="tile__label">{locale === 'ar' ? 'المتبقّي' : 'Kept'}</span>
            <span className="tile__value money" style={{
              color: data.summary.netMinor >= 0 ? 'var(--positive)' : 'var(--negative)',
            }}>
              {privateMoney(data.summary.netMinor, currency, { compact: true })}
            </span>
            <span className="tile__foot">
              {data.summary.incomeMinor > 0
                ? `${Math.round((data.summary.netMinor / data.summary.incomeMinor) * 100)}% ${locale === 'ar' ? 'من الدخل' : 'of income'}`
                : (locale === 'ar' ? 'لا دخل مسجّل' : 'no income recorded')}
            </span>
          </div>

          <div className="tile">
            <span className="tile__label">{locale === 'ar' ? 'المعدل اليومي' : 'Per day'}</span>
            <span className="tile__value money">
              {money(data.summary.dailyAverageMinor, currency, { compact: true })}
            </span>
            <span className="tile__foot">
              {locale === 'ar' ? 'متوسط الإنفاق' : 'average spend'}
            </span>
          </div>
        </div>
      )}

      {data && data.insights.length === 1 && data.insights[0]!.kind === 'no_data' && (
        <Empty
          icon="📊"
          title={data.summary && data.summary.transactionCount === 0
            ? (locale === 'ar' ? `لا إنفاق في ${data.period.label}` : `Nothing spent · ${data.period.label}`)
            : t('noInsightsYet')}
          body={data.summary && data.summary.transactionCount === 0
            ? (locale === 'ar' ? 'جرّب فترة أطول.' : 'Try a longer period.')
            : t('noInsightsBody')}
        />
      )}

      {/* Follows the picker above it. The chart that used to sit here always
          showed the last six months, so the one interactive control on the
          screen appeared to govern nothing. */}
      {data?.breakdown && data.breakdown.buckets.length > 0 && (
        <>
          <SectionTitle>{GRANULARITY_TITLE[data.breakdown.granularity][locale === 'ar' ? 'ar' : 'en']}</SectionTitle>
          <Card>
            <BreakdownChart
              buckets={data.breakdown.buckets}
              granularity={data.breakdown.granularity}
              currency={currency}
            />
          </Card>
        </>
      )}

      {/* Where it went, held against last period.
          Home carries a category breakdown too, and this is not a copy of it:
          Home has no previous cycle to compare with, and the comparison is the
          reason this screen has a period picker at all. */}
      {data && data.byCategory.length > 0 && (
        <>
          <SectionTitle>{locale === 'ar' ? 'أين ذهب' : 'Where it went'}</SectionTitle>
          <Card>
            {(() => {
              /*
               * The listed categories plus whatever they leave out.
               *
               * The API sends the top eight; a bar built from those alone
               * would present a fraction of the spending as the whole of it,
               * with every share overstated to make it add up. The remainder
               * is one segment, so the bar is the period and the percentages
               * are true.
               */
              const listed = data.byCategory.reduce((s, c) => s + c.totalMinor, 0);
              const rest = Math.max(0, data.summary.spentMinor - listed);
              const total = listed + rest;
              const segments = [
                ...data.byCategory.map((c) => ({
                  key: c.category,
                  label: categoryLabel(c.category),
                  color: categoryMeta(c.category).color,
                  totalMinor: c.totalMinor,
                  changePct: c.changePct,
                })),
                // Named for what it is. "Other" is already a real category with
                // its own colour in the list above, so this cannot borrow that
                // word without claiming to be it.
                ...(rest > 0 ? [{
                  key: '__rest__',
                  label: locale === 'ar' ? 'باقي الفئات' : 'Everything else',
                  color: 'var(--border-strong)',
                  totalMinor: rest,
                  changePct: null as number | null,
                }] : []),
              ];
              const share = (v: number) => (total > 0 ? (v / total) * 100 : 0);

              return (
                <>
                  <div className="stackbar" role="img"
                       aria-label={segments
                         .map((s) => `${s.label} ${Math.round(share(s.totalMinor))}%`)
                         .join(', ')}>
                    {segments.map((s) => (
                      <span key={s.key} className="stackbar__seg"
                            style={{ width: `${share(s.totalMinor)}%`, background: s.color }} />
                    ))}
                  </div>

                  <div className="legend" style={{ marginTop: 12 }}>
                    {segments.map((s) => (
                      <div key={s.key} className="legend__row">
                        <span className="legend__swatch" style={{ background: s.color }} aria-hidden />
                        <span className="legend__name">{s.label}</span>
                        {s.changePct !== null && <Delta pct={s.changePct} spend />}
                        <span className="legend__share">{Math.round(share(s.totalMinor))}%</span>
                        <span className="legend__value money">
                          {money(s.totalMinor, currency, { compact: true })}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </Card>
        </>
      )}

      {data && data.topMerchants.length > 0 && (
        <>
          <SectionTitle>{locale === 'ar' ? 'الأكثر إنفاقاً' : 'Most spent at'}</SectionTitle>
          <Card>
            {data.topMerchants.map((m) => (
              <div key={m.merchant} className="row row--between" style={{ paddingBlock: 9, gap: 12 }}>
                <span className="small truncate grow">{m.display ?? m.merchant}</span>
                <span className="tiny muted" style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  ×{m.count}
                </span>
                <span className="small strong money" style={{ flexShrink: 0 }}>
                  {money(m.totalMinor, currency, { compact: true })}
                </span>
              </div>
            ))}
          </Card>
        </>
      )}

      {/* The days that broke the pattern.
          A title alone said "Unusually high spending on 2026-07-27" — a raw
          key, no amount, and no way to see what was bought. Each row now
          carries what the day cost and how far above a normal day that is,
          and opens the transactions behind it. */}
      {data && (() => {
        const spikes = data.insights.filter((i) => i.kind === 'anomaly');
        if (!spikes.length) return null;
        return (
          <>
            <SectionTitle>{locale === 'ar' ? 'أيام غير معتادة' : 'Unusual days'}</SectionTitle>
            <Card>
              {spikes.map((i) => {
                const day = i.evidence.day as string | undefined;
                const spent = i.evidence.valueMinor as number | undefined;
                const usual = i.evidence.comparisonMinor as number | undefined;
                const ids = (i.evidence.transactionIds as string[] | undefined) ?? [];
                const times = usual && usual > 0 && spent ? spent / usual : null;
                return (
                  <button
                    key={i.id}
                    type="button"
                    className="row row--between"
                    style={{ width: '100%', gap: 10, paddingBlock: 10, background: 'none', textAlign: 'start' }}
                    onClick={() => day && setSpikeDay({ day, count: ids.length })}
                  >
                    <span className="grow" style={{ minWidth: 0 }}>
                      <span className="small strong" style={{ display: 'block' }}>
                        {day ? formatDate(day, locale) : (locale === 'ar' ? i.titleAr : i.title)}
                      </span>
                      <span className="tiny muted">
                        {times
                          ? (locale === 'ar'
                              ? `${times.toFixed(1)}× يوم عادي · ${ids.length} معاملة`
                              : `${times.toFixed(1)}× a normal day · ${ids.length} ${ids.length === 1 ? 'transaction' : 'transactions'}`)
                          : (locale === 'ar' ? i.bodyAr : i.body)}
                      </span>
                    </span>
                    <span className="row" style={{ gap: 8, flexShrink: 0, alignItems: 'baseline' }}>
                      {spent != null && (
                        <span className="small strong money">
                          {money(spent, currency, { compact: true })}
                        </span>
                      )}
                      <span className="tiny muted" aria-hidden>›</span>
                    </span>
                  </button>
                );
              })}
            </Card>
          </>
        );
      })()}

      {spikeDay && (
        <DaySheet day={spikeDay.day} currency={currency} onClose={() => setSpikeDay(null)} />
      )}

      {subs && subs.subscriptions.length > 0 && (
        <>
          <SectionTitle action={<ProvenanceBadge kind="estimated" />}>{t('subscriptions')}</SectionTitle>
          <Card>
            <div className="row row--between" style={{ paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
              <div>
                <div className="tiny muted">{t('perMonth')}</div>
                <div style={{ fontSize: 21, fontWeight: 680, fontVariantNumeric: 'tabular-nums' }}>
                  {money(subs.totals.monthlyMinor, currency)}
                </div>
              </div>
              <div style={{ textAlign: 'end' }}>
                <div className="tiny muted">{t('estimatedAnnual')}</div>
                <div style={{ fontSize: 17, fontWeight: 620, fontVariantNumeric: 'tabular-nums' }}>
                  {money(subs.totals.annualMinor, currency, { compact: true })}
                </div>
              </div>
            </div>

            {subs.subscriptions.map((s) => (
              <div key={s.merchant} className="row row--between" style={{ paddingBlock: 11, gap: 12 }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="small strong truncate">{s.merchantDisplay}</div>
                  <div className="tiny muted">
                    {t('nextCharge')} {formatDate(s.nextExpected, locale)} · {money(s.estimatedAnnualMinor, currency, { compact: true })}/{locale === 'ar' ? 'سنة' : 'yr'}
                  </div>
                </div>
                <div style={{ textAlign: 'end', flexShrink: 0 }}>
                  <div className="small strong money">{money(s.amountMinor, currency)}</div>
                  <button className="tiny" style={{ color: 'var(--text-tertiary)', background: 'none' }}
                          onClick={() => dismissRecurring(s.merchant)}>
                    {t('notASubscription')}
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {subs && subs.allRecurring.filter((r) => !r.isSubscription).length > 0 && (
        <>
          <SectionTitle>{t('recurringCommitments')}</SectionTitle>
          {/* What this list is and where it came from.
              A section headed "recurring commitments" listing a supermarket
              and a petrol station reads as a mistake until you know nothing
              was declared — these are charges the app noticed repeating, not
              a list anybody entered. */}
          <p className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.6 }}>
            {locale === 'ar'
              ? 'رسوم تكرّرت ثلاث مرات على الأقل عند التاجر نفسه، بمبلغ متقارب وفاصل زمني منتظم. لم تُعلن، بل استُنتجت من حركاتك — والاشتراكات معروضة أعلاه بشكل منفصل.'
              : 'Charges the app has seen at least three times from the same merchant, for a similar amount, at a steady interval. Nothing here was declared — it is inferred from your transactions, and subscriptions are listed separately above.'}
          </p>
          <Card>
            {/* Sum only the rows shown below. The API's `commitments` total
                covers every recurring charge including subscriptions, so using
                it here would give a header that does not add up to its list. */}
            <div className="row row--between" style={{ paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
              <span className="small muted">{t('perMonth')}</span>
              <span className="strong money">
                {money(
                  subs.allRecurring
                    .filter((r) => !r.isSubscription)
                    .reduce((sum, r) => sum + r.monthlyEquivalentMinor, 0),
                  currency,
                )}
              </span>
            </div>
            {subs.allRecurring.filter((r) => !r.isSubscription).map((r) => (
              <div key={r.merchant} className="row row--between" style={{ paddingBlock: 9 }}>
                <span className="small truncate">{r.merchantDisplay}</span>
                <span className="small money">{money(r.amountMinor, currency)}</span>
              </div>
            ))}
          </Card>
        </>
      )}

      <p className="tiny muted center" style={{ marginTop: 24, lineHeight: 1.6 }}>
        {locale === 'ar'
          ? 'كل رقم هنا محسوب من حركاتك المسجلة. التقديرات موسومة بذلك.'
          : 'Every figure here is computed from your recorded transactions. Projections are labelled as estimates.'}
      </p>
    </div>
    </PullToRefresh>
  );
}


/**
 * The wrap for the cycle that just ended.
 *
 * Collapsed to a single line until opened: it is a look back, and a look back
 * should never be the first thing between someone and today's numbers.
 */
function WrapCard(): JSX.Element | null {
  const { locale, categoryLabel, user } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';
  const { data } = useFetch<{ available: boolean; wrap: Wrap | null }>('/wrap?offset=1');
  const [open, setOpen] = useState(false);

  if (!data?.available || !data.wrap) return null;
  const w = data.wrap;

  return (
    <>
      <button className="btn btn--secondary btn--block btn--sm" style={{ marginBottom: 12 }}
              onClick={() => setOpen(true)}>
        {ar ? 'ملخّص الدورة السابقة' : 'Last cycle, wrapped'}
      </button>

      {open && (
        <Sheet title={ar ? 'الدورة السابقة' : 'Last cycle'} onClose={() => setOpen(false)}>
          <div className="stack">
            {w.headline && (
              <Card>
                <div className="strong" style={{ fontSize: 18, lineHeight: 1.45 }}>
                  {ar ? w.headline.ar : w.headline.en}
                </div>
              </Card>
            )}

            <Card>
              <div className="row row--between">
                <div>
                  <div className="tiny muted">{ar ? 'أنفقت' : 'Spent'}</div>
                  <div className="strong" style={{ fontSize: 22 }}>
                    {money(w.spentMinor, currency)}
                  </div>
                </div>
                <div style={{ textAlign: ar ? 'left' : 'right' }}>
                  <div className="tiny muted">{ar ? 'ادّخرت' : 'Kept'}</div>
                  <div className="strong" style={{
                    fontSize: 22, color: w.savedMinor >= 0 ? 'var(--positive)' : 'var(--negative)',
                  }}>
                    {privateMoney(w.savedMinor, currency)}
                  </div>
                </div>
              </div>
              {w.changePct !== null && (
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  {w.changePct >= 0
                    ? (ar ? `أعلى بنسبة ${Math.round(w.changePct)}٪ عن الدورة السابقة`
                          : `${Math.round(w.changePct)}% more than the cycle before`)
                    : (ar ? `أقل بنسبة ${Math.abs(Math.round(w.changePct))}٪ عن الدورة السابقة`
                          : `${Math.abs(Math.round(w.changePct))}% less than the cycle before`)}
                </div>
              )}
            </Card>

            {w.topCategories.length > 0 && (
              <Card className="card--flush">
                <div className="field__label" style={{ padding: '10px 14px 4px' }}>
                  {ar ? 'أين ذهب المال' : 'Where it went'}
                </div>
                {w.topCategories.map((c) => (
                  <div key={c.category} className="row row--between" style={{ padding: '9px 14px' }}>
                    <span className="small">{categoryLabel(c.category)}</span>
                    <span className="small strong">
                      {money(c.totalMinor, currency)}
                      <span className="muted"> · {Math.round(c.sharePct)}%</span>
                    </span>
                  </div>
                ))}
              </Card>
            )}

            {w.topMerchants.length > 0 && (
              <Card className="card--flush">
                <div className="field__label" style={{ padding: '10px 14px 4px' }}>
                  {ar ? 'الأكثر إنفاقاً' : 'Most spent at'}
                </div>
                {w.topMerchants.map((m) => (
                  <div key={m.merchant} className="row row--between" style={{ padding: '9px 14px' }}>
                    <span className="small">{m.display ?? m.merchant}</span>
                    <span className="small strong">
                      {money(m.totalMinor, currency)}
                      <span className="muted"> · {m.count}×</span>
                    </span>
                  </div>
                ))}
              </Card>
            )}

            <div className="tiny muted center" style={{ lineHeight: 1.6 }}>
              {ar
                ? `${w.transactionCount} معاملة في هذه الدورة`
                : `${w.transactionCount} transactions this cycle`}
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}
